import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { money } from "@/dealership/lib/api";
import { toast } from "sonner";
import PageHeader from "@/dealership/components/PageHeader";
import AppraisalForm, { emptyAppraisal, appraisalToBody, APPRAISAL_STEPS } from "@/dealership/components/AppraisalForm";
import { Stepper } from "@/components/lux";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Camera } from "lucide-react";

export default function Appraisals() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyAppraisal);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const { data: appraisals = [], isLoading } = useQuery({
    queryKey: ["appraisals"],
    queryFn: async () => (await api.get("/appraisals")).data,
  });

  const createMut = useMutation({
    mutationFn: async () => (await api.post("/appraisals", appraisalToBody(form))).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["appraisals"] });
      toast.success("Appraisal created — add photos and run the market scan");
      setOpen(false);
      setForm(emptyAppraisal);
      navigate(`/dealership/appraisals/${d.id}`);
    },
    onError: () => toast.error("Failed to create appraisal"),
  });

  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/appraisals/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["appraisals"] });
      toast.success("Appraisal deleted");
    },
  });

  return (
    <div className="p-8">
      <PageHeader
        title="Appraisals"
        subtitle="Appraise client vehicles with AI photo inspection and a live Ontario market scan."
        action={
          <Button onClick={() => { setForm(emptyAppraisal); setStep(0); setOpen(true); }} data-testid="new-appraisal-button">
            <Plus className="h-4 w-4 mr-2" /> New Appraisal
          </Button>
        }
      />

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Client</TableHead>
              <TableHead>Vehicle</TableHead>
              <TableHead className="text-right">Trade-In Range</TableHead>
              <TableHead className="text-right">Retail Value</TableHead>
              <TableHead>Photos</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : appraisals.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">No appraisals yet. Create one to appraise a client's car.</TableCell></TableRow>
            ) : (
              appraisals.map((a) => {
                const ap = a.appraisal;
                return (
                  <TableRow key={a.id} data-testid={`appraisal-row-${a.id}`} className="cursor-pointer" onClick={() => navigate(`/dealership/appraisals/${a.id}`)}>
                    <TableCell className="font-semibold">{a.client_name}
                      <span className="block text-xs text-muted-foreground font-normal">{a.client_phone}</span>
                    </TableCell>
                    <TableCell>
                      {a.year} {a.make} {a.model}
                      <span className="block text-xs text-muted-foreground">{a.trim} · {Number(a.odometer).toLocaleString()} km</span>
                    </TableCell>
                    <TableCell className="text-right font-mono-data font-semibold">
                      {ap ? `${money(ap.trade_in_low)} – ${money(ap.trade_in_high)}` : "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono-data">{ap ? money(ap.retail_value) : "—"}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                        <Camera className="h-3.5 w-3.5" /> {a.photo_count || 0}
                      </span>
                    </TableCell>
                    <TableCell>
                      {a.appraise_status === "running" ? (
                        <Badge className="bg-blue-500/12 text-blue-400 border-0">Scanning…</Badge>
                      ) : a.status === "appraised" ? (
                        <Badge className="bg-green-500/12 text-green-400 border-0">Appraised</Badge>
                      ) : (
                        <Badge className="bg-zinc-500/12 text-zinc-400 border-0">Draft</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(a.created_at).toLocaleDateString("en-CA", { month: "short", day: "numeric" })}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); delMut.mutate(a.id); }} data-testid={`delete-appraisal-${a.id}`}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Appraisal</DialogTitle></DialogHeader>
          <Stepper steps={APPRAISAL_STEPS} current={step} onStep={setStep} testid="appraisal-stepper" />
          <AppraisalForm form={form} set={set} step={step} />
          <DialogFooter>
            <Button variant="ghost" onClick={() => (step === 0 ? setOpen(false) : setStep(step - 1))}>
              {step === 0 ? "Cancel" : "Back"}
            </Button>
            {step < APPRAISAL_STEPS.length - 1 ? (
              <Button
                data-testid="appraisal-next"
                onClick={() => {
                  if (step === 0 && !form.client_name) return toast.error("Client name is required");
                  if (step === 1 && (!form.year || !form.make || !form.model)) return toast.error("Year, make and model are required");
                  setStep(step + 1);
                }}
              >
                Continue
              </Button>
            ) : (
              <Button
                onClick={() => createMut.mutate()}
                disabled={!form.client_name || !form.year || !form.make || !form.model || createMut.isPending}
                data-testid="save-appraisal-button"
              >
                Create Appraisal
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
